import { toast } from "react-toastify";
import Loader from "src/components/common/Loader/Loader";
import { useDeleteRegularClassSchedulesSimulate } from "src/services/react-query-apis/delete-regular-class-schedules-simulate";
import { useDeleteRegularClassSchedules } from "src/services/react-query-apis/delete-regular-class-schedules";
import useCoach from "src/hooks/useCoach";
import useClassLevel from "src/hooks/useClassLevel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RescheduleAttendanceWarning from "src/components/admin/masterMaintenance/time-table-master/reschedule-attendance-warning";
import moment from "moment";
import { getCurrentMonth } from "src/services/auth/token";

const TimeTableDeleteConfirm = ({ schedule, onClose }) => {
  const classLevel = useClassLevel();
  const coach = useCoach();

  const deleteRegularClassSchedulesSimulateQuery =
    useDeleteRegularClassSchedulesSimulate({
      ...{ id: schedule.id },
      queryConfig: {
        staleTime: 0,
      },
    });

  const mutationConfig = {
    onSuccess: async data => {
      toast.success(data.message);
      setTimeout(() => {
        onClose();
      }, 10);
    },
  };

  const deleteRegularClassSchedulesMutation = useDeleteRegularClassSchedules({
    mutationConfig,
  });

  if (deleteRegularClassSchedulesSimulateQuery.isFetching) {
    return <Loader message="データを取得中..." />;
  }

  if (deleteRegularClassSchedulesSimulateQuery.isError) {
    return (
      <>
        <div className="text-danger text-center mb-3 p-1 rounded bg-light border fw-bold">
          {deleteRegularClassSchedulesSimulateQuery.error.response.data.message}
        </div>
        <div className="text-end">
          <button className="btn btn-primary" onClick={onClose}>
            閉じる
          </button>
        </div>
      </>
    );
  }

  const affectedStudents =
    deleteRegularClassSchedulesSimulateQuery.data?.data.affected_members || [];

  const hasAffectedStudents = affectedStudents.length > 0;

  const rescheduleAttendances =
    deleteRegularClassSchedulesSimulateQuery.data?.data
      .affected_rescheduled_attendances || [];

  const hasRescheduleAttendances = rescheduleAttendances.length > 0;

  return (
    <div className="d-flex justify-content-center">
      <div className="d-flex flex-column w-100 time-table-form">
        <div>
          <div
            className="alert alert-warning mb-3 text-center p-1"
            role="alert"
          >
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            この変更は{" "}
            {moment(getCurrentMonth())
              .add(1, "month")
              .format("YYYY年MM月")}{" "}
            から適用されます
          </div>
          {/* Schedule Details Card */}
          <div
            className="card shadow-sm mb-4 mx-auto"
            style={{ maxWidth: "500px" }}
          >
            <div className="card-header bg-light py-2">
              <h6 className="text-center mb-0 fw-bold">スケジュール詳細</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="day-label text-center mb-3 p-1 rounded bg-light border fw-bold">
                    {schedule.day_week}
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">開始時間</div>
                  <div className="fw-bold">{schedule.start_time}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">終了時間</div>
                  <div className="fw-bold">{schedule.end_time}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">クラスレベル</div>
                  <div className="fw-bold">
                    {classLevel.list.find(
                      level => level.id === schedule.class_level_id
                    )?.in_text || "-"}
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">コーチ</div>
                  <div className="fw-bold">
                    {coach.list.find(coach => coach.id === schedule.coach_id)
                      ?.name || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Affected Students Warning */}
          {hasAffectedStudents && (
            <div className="alert alert-warning shadow-sm mb-4">
              <div className="d-flex align-items-center mb-3">
                <h6 className="mb-0 fw-bold">
                  以下の生徒の翌月以降のスケジュールを変更してください：
                  <br />
                  <div style={{ fontSize: "14px" }}>
                    ※クラスがない場合は、クラスを作成する必要があります
                  </div>
                </h6>
              </div>
              <div className="list-group">
                {affectedStudents.map(student => (
                  <div
                    key={student.id}
                    className="list-group-item list-group-item-action"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold">{student.name}</div>
                        <div className="text-muted small">
                          {student.member_code}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {hasRescheduleAttendances && (
          <RescheduleAttendanceWarning
            rescheduleAttendances={rescheduleAttendances}
          />
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2 justify-content-center">
          <button className="btn btn-secondary px-4" onClick={onClose}>
            閉じる
          </button>
          {!hasAffectedStudents && !hasRescheduleAttendances && (
            <button
              className="btn btn-danger px-4"
              onClick={() => {
                deleteRegularClassSchedulesMutation.mutate({
                  id: schedule.id,
                });
              }}
              disabled={deleteRegularClassSchedulesMutation.isPending}
            >
              {deleteRegularClassSchedulesMutation.isPending
                ? "処理中..."
                : "削除"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeTableDeleteConfirm;
